import React, { useEffect } from "react";
import { Form, Input, Button, Card, Spin, Row, Col } from "antd";
import { Save, Info } from "lucide-react";
import UploadImage from "../../components/UploadImage";
import TinyMce from "../../../utils/TinyMCE";
import { useGetCmsConfig, useSaveCmsConfig } from "../../../hooks/api/cms/hooks";
import { useMessageApi } from "../../../provider/AntdNotification";

const AboutPageCMS: React.FC = () => {
  const [form] = Form.useForm();
  const messageApi = useMessageApi();
  const { data: configData, isLoading } = useGetCmsConfig<any>("ABOUT_PAGE");
  const saveMutation = useSaveCmsConfig<any>({
    page: "ABOUT_PAGE",
    onSuccess: () => {
      messageApi.success("About Page configuration saved successfully.");
    },
    onError: (err: any) => messageApi.error(err?.message ?? "Failed to save configuration"),
  });

  useEffect(() => {
    if (configData?.data) {
      const cfg = configData.data;
      form.setFieldsValue({
        pageTitle: cfg.pageTitle || "",
        pageSubtitle: cfg.pageSubtitle || "",
        heroImage: cfg.heroImage || "",
        introTitle: cfg.introTitle || cfg.intro?.title || "",
        introImage: cfg.introImage || cfg.intro?.image || "",
        introContent: cfg.introContent || cfg.mainContent || "",
        exhibitionTitle: cfg.exhibitionTitle || cfg.exhibitions?.title || "",
        exhibitionImage: cfg.exhibitionImage || cfg.exhibitions?.image || "",
        exhibitionContent: cfg.exhibitionContent || cfg.exhibitions?.description || "",
        historyTitle: cfg.historyTitle || cfg.history?.title || "",
        historySubtitle: cfg.historySubtitle || cfg.history?.subtitle || "",
        historyImage: cfg.historyImage || cfg.history?.image || "",
        historyContent: cfg.historyContent || cfg.secondaryContent || "",
      });
    }
  }, [configData, form]);

  const onFinish = (values: any) => {
    const formattedValues = {
      ...values,
      heroImage:
        Array.isArray(values.heroImage) && values.heroImage.length > 0
          ? values.heroImage[0]
          : values.heroImage,
      introImage:
        Array.isArray(values.introImage) && values.introImage.length > 0
          ? values.introImage[0]
          : values.introImage,
      exhibitionImage:
        Array.isArray(values.exhibitionImage) && values.exhibitionImage.length > 0
          ? values.exhibitionImage[0]
          : values.exhibitionImage,
      historyImage:
        Array.isArray(values.historyImage) && values.historyImage.length > 0
          ? values.historyImage[0]
          : values.historyImage,
    };
    saveMutation.mutate(formattedValues);
  };

  if (isLoading) {
    return (
      <div className="flex h-[50vh] items-center justify-center">
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div className="w-full space-y-6 pb-12">
      <div className="w-full">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-semibold text-slate-800 flex items-center gap-2">
              <Info className="text-primary" size={24} /> About Page Content Management
            </h1>
            <p className="text-sm text-slate-500 mt-1">
              Customize page title, hero image, intro section, exhibition section, and final history story section.
            </p>
          </div>
        </div>
      </div>

      <Form
        form={form}
        layout="vertical"
        onFinish={onFinish}
        className="flex flex-col gap-6"
      >
        {/* HERO SECTION */}
        <Card
          title={
            <span className="font-semibold text-slate-800">
              Page Header & Hero Banner
            </span>
          }
        >
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="pageTitle" label="Page Title">
                <Input placeholder="e.g. About Art ParkS" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="pageSubtitle" label="Page Subtitle">
                <Input.TextArea
                  rows={2}
                  placeholder="e.g. Discover top-quality contemporary sculpture..."
                />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name="heroImage" label="Main Hero Banner Image">
            <Form.Item name="heroImage" noStyle>
              <UploadImage count={1} listType="picture-card" />
            </Form.Item>
          </Form.Item>
        </Card>

        {/* INTRO SECTION */}
        <Card
          title={
            <span className="font-semibold text-slate-800">
              1. Introductory Section
            </span>
          }
        >
          <Row gutter={24}>
            <Col span={16}>
              <Form.Item name="introTitle" label="Section Heading">
                <Input placeholder="e.g. Welcome to Art Parks International..." />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="introImage" label="Section Side Image">
                <Form.Item name="introImage" noStyle>
                  <UploadImage count={1} listType="picture-card" />
                </Form.Item>
              </Form.Item>
            </Col>
          </Row>
          <Form.Item
            name="introContent"
            label="Intro Content Editor (Paste intro paragraphs here)"
          >
            <TinyMce />
          </Form.Item>
        </Card>

        {/* EXHIBITIONS SECTION */}
        <Card
          title={
            <span className="font-semibold text-slate-800">
              2. Annual Sculpture Exhibition & Guest List Section
            </span>
          }
        >
          <Row gutter={24}>
            <Col span={16}>
              <Form.Item name="exhibitionTitle" label="Section Heading">
                <Input placeholder="e.g. Annual Sculpture Exhibition" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="exhibitionImage" label="Section Side Image">
                <Form.Item name="exhibitionImage" noStyle>
                  <UploadImage count={1} listType="picture-card" />
                </Form.Item>
              </Form.Item>
            </Col>
          </Row>
          <Form.Item
            name="exhibitionContent"
            label="Exhibition Content & Guest List Editor (Paste intro & bullet list here)"
          >
            <TinyMce />
          </Form.Item>
        </Card>

        {/* HISTORY / FOUNDERS STORY SECTION (Matches Screenshot UI) */}
        <Card
          title={
            <span className="font-semibold text-slate-800">
              3. History & Founder Story Section (Top Title & Subtitle + Row Image/Editor)
            </span>
          }
        >
          <Form.Item name="historyTitle" label="Section Title (Displays on Top)">
            <Input placeholder="e.g. The Sculpture Park was founded in 1998 with the help of John Mills..." />
          </Form.Item>

          <Form.Item name="historySubtitle" label="Short Description / Subtitle (Displays on Top below Title)">
            <Input.TextArea
              rows={3}
              placeholder="e.g. Welcome to Art Parks International, your gateway to an extraordinary collection..."
            />
          </Form.Item>

          <Row gutter={24}>
            <Col span={8}>
              <Form.Item name="historyImage" label="Left Side Image">
                <Form.Item name="historyImage" noStyle>
                  <UploadImage count={1} listType="picture-card" />
                </Form.Item>
              </Form.Item>
            </Col>
            <Col span={16}>
              <Form.Item
                name="historyContent"
                label="Right Side Story Editor (Paste full story paragraphs here)"
              >
                <TinyMce />
              </Form.Item>
            </Col>
          </Row>
        </Card>
      </Form>

      {/* Sticky Footer Save Bar */}
      <div className="sticky bottom-4 bg-white/95 backdrop-blur border border-slate-200 py-3 px-6 rounded-xl z-40 flex items-center justify-end shadow-lg">
        <Button
          type="primary"
          size="large"
          icon={<Save size={18} />}
          loading={saveMutation.isPending}
          onClick={() => form.submit()}
          className="!bg-primary flex items-center gap-2 px-8 shadow-md"
        >
          Save Configuration
        </Button>
      </div>
    </div>
  );
};

export default AboutPageCMS;
